import type { SVGProps } from "react";

type Props = SVGProps<SVGSVGElement>;

/**
 * Marks for the integrations ZopDesk has today. Brand marks are simplified;
 * IMAP, Acefone and "your own system" use generic glyphs. Decorative only:
 * the integration's name is always shown next to its mark.
 */

const frame = (bg: string, children: React.ReactNode, className?: string) => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <rect width="40" height="40" rx="9" fill={bg} />
    {children}
  </svg>
);

/** Gmail — red/white envelope with M stroke */
export function GmailMark(props: Props) {
  return frame(
    "#fef2f2",
    <g transform="translate(9, 11)">
      <path
        d="M2 0h18a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2z"
        fill="#ffffff"
        stroke="#EA4335"
        strokeWidth="1.4"
      />
      <path
        d="M0.6 2.5 L11 10 L21.4 2.5"
        stroke="#EA4335"
        strokeWidth="1.6"
        fill="none"
        strokeLinejoin="round"
      />
    </g>,
    props.className,
  );
}

/** Microsoft / 365 — 4-color square grid */
export function MicrosoftMark(props: Props) {
  return frame(
    "#f4f4f5",
    <g transform="translate(10, 10)">
      <rect width="9" height="9" fill="#F25022" />
      <rect x="11" width="9" height="9" fill="#7FBA00" />
      <rect y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </g>,
    props.className,
  );
}

/** Shopify — green bag with S stroke */
export function ShopifyMark(props: Props) {
  return frame(
    "#ecfdf5",
    <g transform="translate(10, 9)">
      <path
        d="M18.5 4.2c-.04-.29-.25-.44-.44-.46-.2-.01-3.74-.27-3.74-.27L11.54.77c-.25-.25-.75-.17-.96-.11 0 0-.55.17-1.45.45-.85-2.45-2.37-2.34-3.19-2.18-2.18.7-3.14 3.08-3.46 4.33L.4 3.67c-.76.24-.78.26-.88.97L.63 20.75 14.75 24l7.42-2.84L19.93 5.37c-.07-.65-.51-.97-.95-1.17h-.48zM8 1.87c.7-.21 1.2-.5 1.64-.72.03.5.05 1.2 0 2.04-.7.22-1.45.45-2.2.69.42-1.6.87-2.06.56-2.01zm-1.7 1.66c.53-.17 1.42-.41 2.22-.66.05-.86.05-1.58.02-2.1C7.47.97 6.3 1.77 6.3 3.54zm5.07-1.77c-.05-.2-.17-.5-.35-.77-.58.1-1.14.29-1.67.46.07-.99.09-1.94.06-2.74.86.51 1.4 1.57 1.57 3.04.14.01.26 0 .4-.01z"
        fill="#95BF47"
      />
      <path
        d="M18.06 3.74c-.2-.01-3.74-.27-3.74-.27L11.54.77C11.45.68 11.33.62 11.2.58v23.42L22.17 21.16 19.93 5.37c-.07-.65-.51-.97-.95-1.17-.3-.2-.65-.44-.92-.46z"
        fill="#5E8E3E"
      />
    </g>,
    props.className,
  );
}

/** Zopping — sibling product. Lightning-Z in brand gradient */
export function ZoppingMark(props: Props) {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={props.className}
    >
      <defs>
        <linearGradient id="zop-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#4f46e5" />
          <stop offset="100%" stopColor="#a855f7" />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="9" fill="#eef2ff" />
      <path
        d="M13 12h14l-14 16h14"
        stroke="url(#zop-g)"
        strokeWidth="2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** Claude (Anthropic) — sun-ray mark in warm cream */
export function ClaudeMark(props: Props) {
  return frame(
    "#faf8f5",
    <g transform="translate(8, 8)">
      <path
        d="M4.709 15.955 9.45 12.77l.08-.233L9.45 12.4l-.238-.002-.814-.05-2.78-.075-2.41-.1-2.334-.126-.587-.125L0 11.216l.056-.365.496-.331.708.063 1.568.107 2.352.163 1.706.1 2.527.263h.401l.057-.162-.137-.1-.106-.1-2.424-1.643-2.627-1.74-1.375-1-.744-.508-.375-.475-.163-1.037.675-.743.906.06.231.063.92.708 1.962 1.52 2.562 1.888.376.312.15-.106.02-.075-.169-.282-1.4-2.531-1.494-2.581-.666-1.068-.175-.642a3.093 3.093 0 0 1-.109-.76L1.945.633 2.309.457l.876.117.375.33.549 1.252.887 1.97 1.374 2.68.405.8.215.744.082.228h.14V8.45l.113-1.512.21-1.857.201-2.389.068-.674.327-.797.651-.43.508.245.42.6-.056.39-.237 1.55-.47 2.442-.307 1.632h.177l.204-.206.825-1.096 1.389-1.732.612-.69.712-.76.459-.362h.866l.633.947-.286.975-.891 1.13-.735.954-1.055 1.426-.656 1.13.06.09.157-.013 2.387-.507 1.287-.232 1.538-.262.692.321.077.324-.27.66-1.618.4-1.9.381-2.825.67-.034.025.038.053 1.275.12.544.028h1.338l2.496.183.652.435.388.533-.068.418-1.01.512-1.362-.324-3.18-.753-1.095-.275h-.153v.094l.908.892 1.662 1.5 2.075 1.925.105.478-.267.38-.28-.037-1.817-1.38-.701-.619-1.588-1.324h-.104v.138l.367.537 1.927 2.89.098.885-.139.289-.493.174-.546-.1-1.119-1.562-1.154-1.768-.931-1.586-.114.065-.551 5.908-.257.304-.593.226-.491-.376-.26-.606.26-1.19.316-1.549.256-1.232.232-1.53.138-.509-.007-.037-.114.015-1.171 1.609-1.781 2.407-1.411 1.514-.339.135-.586-.304.054-.54.33-.482 1.963-2.496.917-1.195.3-.36.001-.062a4.315 4.315 0 0 1-.203-.148c-.223-.028-3.588 2.326-4.211 2.733-.105.068-.378.156-.408.156-.199 0-.385-.076-.414-.243l-.113-.419.056-.277.197-.19c.95-.592 2.018-1.284 3.18-2.065z"
        fill="#D97757"
      />
    </g>,
    props.className,
  );
}

/** WhatsApp: white chat bubble with handset on WhatsApp green */
export function WhatsAppMark(props: Props) {
  return frame(
    "#25D366",
    <g transform="translate(8, 8)">
      <path
        d="M12 2.5a9.5 9.5 0 0 0-8.2 14.3L2.5 21.5l4.8-1.3A9.5 9.5 0 1 0 12 2.5z"
        stroke="#ffffff"
        strokeWidth="1.8"
        fill="none"
        strokeLinejoin="round"
      />
      <path
        d="M8.6 7.6c.3-.6.6-.6.9-.6h.6c.2 0 .4.1.5.4l.8 1.9c.1.2 0 .5-.1.7l-.6.7c-.1.2-.1.4 0 .5.8 1.4 1.9 2.4 3.3 3.1.2.1.4.1.5-.1l.7-.8c.2-.2.4-.2.6-.1l1.9.9c.2.1.4.3.3.6l-.1.6c-.1.6-.9 1.3-1.7 1.4-1 .1-2.7-.3-4.6-2-1.7-1.6-2.6-3.2-2.8-4.3-.2-.8.2-1.6.8-2.3z"
        fill="#ffffff"
      />
    </g>,
    props.className,
  );
}

/** Acefone (phone calls): generic handset; not Acefone's own logo */
export function AcefoneMark(props: Props) {
  return frame(
    "#eef2ff",
    <path
      d="M14.5 11.5c.5-.9 1-1 1.4-1h1c.3 0 .6.2.8.6l1.3 3c.2.4.1.8-.1 1.1l-1 1.2c-.2.3-.2.6 0 .9 1.3 2.2 3 3.8 5.2 5 .3.2.6.1.8-.1l1.1-1.3c.3-.3.7-.4 1-.2l3 1.4c.4.2.6.5.5.9l-.2 1c-.2 1-1.4 2.1-2.7 2.2-1.6.2-4.3-.4-7.3-3.2-2.7-2.5-4.1-5.1-4.4-6.8-.3-1.3.3-2.5 1.3-3.6z"
      fill="#4f46e5"
    />,
    props.className,
  );
}

/** IMAP: any mailbox, generic envelope */
export function ImapMark(props: Props) {
  return frame(
    "#f4f4f5",
    <g transform="translate(9, 12)">
      <rect x="0.7" y="0.7" width="20.6" height="14.6" rx="2" stroke="#52525b" strokeWidth="1.4" fill="#ffffff" />
      <path d="M1.5 2l9.5 7 9.5-7" stroke="#52525b" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    </g>,
    props.className,
  );
}

/** OpenAI: simplified monochrome mark */
export function OpenAIMark(props: Props) {
  return frame(
    "#18181b",
    <g transform="translate(20, 20)" stroke="#ffffff" strokeWidth="1.6" fill="none">
      {[0, 60, 120, 180, 240, 300].map((r) => (
        <rect key={r} x="-3.2" y="-10" width="6.4" height="10" rx="3.2" transform={`rotate(${r})`} />
      ))}
    </g>,
    props.className,
  );
}

/** Your own system: code brackets, for the lookup and ticket actions */
export function CustomMark(props: Props) {
  return frame(
    "#f5f3ff",
    <g stroke="#7c3aed" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 14l-6 6 6 6" />
      <path d="M24 14l6 6-6 6" />
      <path d="M22 12l-4 16" />
    </g>,
    props.className,
  );
}

/** Webhooks / REST API — custom mark */
export function WebhookMark(props: Props) {
  return frame(
    "#f0fdf4",
    <g transform="translate(8, 8)">
      <circle cx="8" cy="8" r="3.2" stroke="#16a34a" strokeWidth="1.6" fill="none" />
      <circle cx="16" cy="8" r="3.2" stroke="#16a34a" strokeWidth="1.6" fill="none" />
      <circle cx="12" cy="18" r="3.2" stroke="#16a34a" strokeWidth="1.6" fill="none" />
      <path
        d="M10 10.5 L7 17 M14 10.5 L17 17 M8 8 L16 8"
        stroke="#16a34a"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </g>,
    props.className,
  );
}

export const integrations = [
  { Mark: GmailMark, name: "Gmail", category: "Email · sign in with Google" },
  { Mark: MicrosoftMark, name: "Microsoft 365", category: "Email · sign in with Microsoft" },
  { Mark: ImapMark, name: "IMAP and forwarding", category: "Email · any mailbox, checked every 30s" },
  { Mark: WhatsAppMark, name: "WhatsApp", category: "Messages · Meta Cloud API" },
  { Mark: AcefoneMark, name: "Acefone", category: "Phone · calls, click-to-call, recordings" },
  { Mark: ShopifyMark, name: "Shopify", category: "Store · orders and LTV in the sidebar" },
  { Mark: ZoppingMark, name: "Zopping", category: "Store · orders in the sidebar" },
  { Mark: OpenAIMark, name: "OpenAI", category: "AI · your own API key" },
  { Mark: ClaudeMark, name: "Anthropic (Claude)", category: "AI · your own API key" },
  { Mark: CustomMark, name: "Customer data lookup", category: "Your system · fields on every ticket" },
  { Mark: CustomMark, name: "Ticket actions", category: "Your system · buttons like Create lead" },
  { Mark: WebhookMark, name: "REST API and webhooks", category: "Tickets, customers, signed events" },
];
